import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Plus, Info } from 'lucide-react';
import { DrugInfoModal } from './DrugInfoModal';

const API = process.env.REACT_APP_BACKEND_URL;

export const DrugSelector = ({ onSelect, selectedDrugs }) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [drugs, setDrugs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedDrug, setSelectedDrug] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/farmaci/categorie`);
      setCategories(res.data);
    } catch (e) {
      console.error('Error loading categories:', e);
    }
  };

  const loadDrugsByCategory = async (catId) => {
    setSelectedCategory(catId);
    try {
      const res = await axios.get(`${API}/api/farmaci/categoria/${catId}`);
      setDrugs(res.data);
    } catch (e) {
      console.error('Error loading drugs:', e);
    }
  };

  const searchDrugs = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/farmaci/search/${query}`);
      setSearchResults(res.data);
    } catch (e) {
      console.error('Error searching drugs:', e);
    }
  };

  const addDrug = (drug) => {
    if (!selectedDrugs.find(d => d.id === drug.id)) {
      onSelect({
        ...drug,
        posologia: drug.posologia_standard,
        durata: drug.durata_consigliata,
        occhio: 'OU'
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2A9FCB]/60" />
        <input
          type="text"
          placeholder="Cerca farmaco..."
          value={searchQuery}
          onChange={(e) => searchDrugs(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg"
        />
        {searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 bg-white border rounded-lg mt-1 shadow-lg z-10 max-h-60 overflow-y-auto">
            {searchResults.map(drug => (
              <button
                key={drug.id}
                onClick={() => addDrug(drug)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex justify-between items-center"
              >
                <div>
                  <span className="font-medium">{drug.nome_commerciale}</span>
                  <span className="text-[#2A9FCB]/70 text-sm ml-2">{drug.principio_attivo}</span>
                </div>
                <Plus className="w-4 h-4 text-green-600" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-2">
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => loadDrugsByCategory(cat.id)}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${
              selectedCategory === cat.id 
                ? 'bg-emerald-600 text-white' 
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            {cat.nome}
          </button>
        ))}
      </div>

      {/* Drugs List */}
      {drugs.length > 0 && (
        <div className="border rounded-lg max-h-60 overflow-y-auto">
          {drugs.map(drug => (
            <div key={drug.id} className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-gray-50">
              <div className="flex-1">
                <span className="font-medium">{drug.nome_commerciale}</span>
                <span className="text-[#2A9FCB]/70 text-sm ml-2">({drug.principio_attivo} {drug.concentrazione})</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedDrug(drug)}
                  className="p-1 hover:bg-blue-100 rounded-lg text-blue-600"
                  title="Info farmaco"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={() => addDrug(drug)}
                  className="p-1 hover:bg-green-100 rounded-lg text-green-600"
                  title="Aggiungi"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drug Info Modal */}
      {selectedDrug && (
        <DrugInfoModal farmaco={selectedDrug} onClose={() => setSelectedDrug(null)} />
      )}
    </div>
  );
};

export default DrugSelector;
